import { RouteProp } from '@react-navigation/core'
import { ParamListBase } from '@react-navigation/native'
import {
  ActivityType,
  LocalMeal as Meal,
  Measurement,
} from '@src/screens/Events/models/events.types'
import { BaseIngredient } from '@src/screens/Ingredients/types'
import { EditableActivity } from '@src/screens/Activities/types'
import { ShareableViews } from '@src/containers/ShareViewModal/constants'
import { ChatbotMessageReportReason } from '@src/types'
import {
  CheckoutKind,
  GroupedProducts,
  ProductSelection,
} from '@src/screens/Marketplace/types/types'
import { ChartTypes } from '@src/components/Timeline/constants'
import {
  ActionGoal,
  ActionGoalCompletion,
  ActionGoalMetricKind,
  ActionGoalOption,
  Activity,
  BillingProduct,
  BillingProductCategory,
  ChatbotConversation,
  ChatConversationMessageTemplateKind,
  CoachesCoachingKind,
  HealthDataReport,
  EhrAppointmentKind,
  EhrAppointmentRecurrence,
  EhrAppointmentRecurrenceCadence,
  EhrAppointmentSchedulingRuleDef,
  EhrCoachSpecialty,
  EhrCoachSpecialtyDef,
  EhrSlot,
  HealthDataMeasurementSource,
  HealthDataMeasurementType,
  InsurancePolicy,
  JournalEntry,
  LegalPolicy,
  Link,
  MealKind,
  Outcome,
  Product,
  Reminder,
  ReminderKind,
  SensorModel,
  Statistic,
  Subscription,
  SubscriptionCheckoutOptionKind,
  SurveysConfigKind,
  TutorialStep,
  UiColor,
  UiIcon,
  UpsellOffer,
  UserInsurancePolicyKind,
} from '@src/types'
import { ProgramType } from '@src/screens/Questionnaire/screens/GoalsQuestionnaireStartScreen'
import { LessonWithContent } from '@src/screens/Learn/models/courses.types'
import { ThresholdType } from '@src/screens/Settings/models/settings.types'
import { DateSelectorType } from '@components'
import {
  GlucoseKetonesEnum,
  MacrosSectionEnum,
  MeasurementEnum,
  ScoreSectionEnum,
  SectionEnum,
} from '@src/screens/Insights/utils/pageConfig'
import { AddressType } from '@src/screens/Marketplace/components/checkout/AddressForm'
import { HistoryView, NoraVoiceModeSource } from '@src/screens/Nora/constants'
import { TutorialGroups, TutorialSensorKind } from '@src/screens/Tutorials/models/tutorials.types'
import { ReactNativeFile } from '@src/utils/image'
import { MeasurementValues } from '@src/screens/Measurements/types'
import { ProductExplanationScreenTypes } from '@screens/Marketplace/components/NewMarketplace/definitions.tsx'
import { AllCoachProfilesQuery } from '@src/graphql/fetchAllCoachProfiles.generated'
import { SpecialtyKey } from '@src/screens/Onboarding/screens/UnifiedOnboardingCoachingTeam/types'
import { SaveTargetKind } from '@src/screens/Ingredients/constants/saveTarget'
import { JournalMeasurement } from '@src/screens/JournalEntries/types'

export type ReferralParams = {
  source: 'Onboarding' | 'Drawer' | 'Account' | 'UpsellOffer'
}

export type IngredientPortionSaveTarget = {
  kind: SaveTargetKind
  ingredientId: string
  messageId?: string
}

export type JournalEntrySaveTarget = {
  kind: SaveTargetKind.ACTION_TUNNEL
  messageId?: string
}

export type ActivitySaveTarget = {
  kind: SaveTargetKind.ACTION_TUNNEL
  messageId?: string
}

export type ActionGoalSaveTarget = {
  kind: SaveTargetKind.ACTION_TUNNEL
  messageId?: string
}

type WithCancelModal =
  | {
      cancel?: boolean
    }
  | undefined

type WithPhotoUpload = {
  photo?: ReactNativeFile
}

type WithForceLandscapeOrientation = {
  forceLandscapeOrientation?: boolean
}

export interface CoursesModuleParams {
  programId: string
  moduleId: string
  locked: boolean
}

type CoachInfoModalBaseParams = {
  photo?: string | null
  fullName: string
  subTitle?: string
  credentials?: string | null
  bio?: string | null
  specialties: Pick<EhrCoachSpecialtyDef, 'key' | 'name'>[]
}

type CoachInfoModalBioParams = CoachInfoModalBaseParams & {
  variant: 'bio'
  showBookVideoCallButton?: never
  showChatAction?: never
  showEditRecurringAction?: never
  showSwitchButton?: never
  canSwitch?: never
}

type CoachInfoModalAssignedCoachParams = CoachInfoModalBaseParams & {
  variant: 'assignedCoach'
  showBookVideoCallButton?: boolean
  showChatAction?: boolean
  showEditRecurringAction?: boolean
  showSwitchButton?: boolean
  canSwitch?: boolean
  coachingKind: CoachesCoachingKind
  firstName?: string
  referralUrl?: string | null
  coachAssignmentId?: string
}

export type CommonStackParamList = {
  Camera: {
    parentScreen: 'AddMeal' | 'EditMeal' | 'SubmitInsuranceCardScreen'
    parentParams?: any
  }
  CoachInfoModal: CoachInfoModalBioParams | CoachInfoModalAssignedCoachParams
  Legal: undefined
  OwnSensorSelection: {
    nextScreen: 'CreateAccountOptions'
    params?: any
  }
}

type MapRouteToScreenAndProps<T extends ParamListBase> = {
  [K in keyof T]: T[K] extends undefined
    ? { screen: K; params?: never }
    : { screen: K; params: T[K] }
}

export type ScreenAndProps<
  T extends ParamListBase = RootStackParamList
> = MapRouteToScreenAndProps<T>[keyof MapRouteToScreenAndProps<T>]

export type VideoCallProduct = Pick<
  Product,
  'id' | 'key' | 'title' | 'price' | 'description' | 'details' | 'icon'
> & {
  ehrSchedulingRule: Pick<
    EhrAppointmentSchedulingRuleDef,
    'appointmentKind' | 'chargeKind' | 'count' | 'key'
  >
}

type SeeAllCoachesBaseParams = {
  selectedSpecialties?: EhrCoachSpecialty[]
  coachingKind: CoachesCoachingKind
  navigationTitle: string
  title: string
  loadMoreButtonText: string
  showFilters: boolean
  selectionAlertMessage?: string
}

type CoachSelectionCallSchedulingParams = {
  appointmentKind: EhrAppointmentKind
  allowBackNavigation?: boolean
  pendingAppointmentId?: string
  product?: VideoCallProduct | null | undefined
  showCoachProfile?: boolean
  alertMessage?: string
}

export type CoachSwitchSeeAllCoachesScreenParams = SeeAllCoachesBaseParams

export type CallSchedulingSeeAllCoachesScreenParams = SeeAllCoachesBaseParams & {
  callSchedulingParams: CoachSelectionCallSchedulingParams
}

type CoachSwitchSpecialtiesFilterModalParams =
  | {
      parentScreen: 'CoachSwitchSeeAllCoachesScreen'
      parentScreenParams: CoachSwitchSeeAllCoachesScreenParams
      selectedSpecialties: EhrCoachSpecialty[]
    }
  | {
      parentScreen: 'CallSchedulingSeeAllCoachesScreen'
      parentScreenParams: CallSchedulingSeeAllCoachesScreenParams
      selectedSpecialties: EhrCoachSpecialty[]
    }

export type CommonAuthorizedStackParamList = {
  AddonCheckout: {
    selections?: {
      0: ProductSelection
    } & ProductSelection[]
    productKey?: string
    quantity?: number
    nextScreen?: {
      screen: keyof CommonAuthorizedStackParamList
      params?: any
    }
  }
  DietitianMenuCheckout: {
    selection: ProductSelection
    goBackOnSuccess?: boolean
    nextScreen?: { screen: 'LabPanelsOrderConfirmed'; params?: any }
    hideDetails?: boolean
    showMicrobiomeTest?: boolean
  }
  LabPanelRecurrence: {
    panelProduct: Product
    upsell?: boolean
    nextScreen?: {
      screen: keyof CommonAuthorizedStackParamList
      params?: any
    }
  }
  CheckoutWithInsuranceOption: {
    product: Product
  }
  DietitianMenuFeedback: undefined
  ChangeAddress: { addressType: AddressType }
  CreatePaymentMethod: undefined
  DiscountCode: { product?: BillingProduct }
  SteloPaymentSuccessful:
    | undefined
    | {
        nextScreen?: {
          screen: keyof CommonAuthorizedStackParamList
          params?: any
        }
      }
  UnifiedOnboardingConfirmation: undefined
  LabPanelsOrderConfirmed?: {
    nextScreen?: { screen: 'UpsellOffer'; params: { upsellOfferIndex: number } }
  }
  SupportAnswer: {
    id: string
  }
  SupportArchive: {
    conversationId: string
  }
  SupportHub:
    | {
        subject?: string
        description?: string
        ticketTopic?: string
      }
    | undefined
  VideoCallConfirmed: {
    meetingAt: string
    coachFullName: string
    coachPhoto?: string | null
    coachCredentials?: string | null
    coachBio?: string | null
    coachSpecialties: Pick<EhrCoachSpecialtyDef, 'key' | 'name'>[]
    info?: string
  }
  DietitianAddonOrderConfirmed: {
    product: VideoCallProduct
    groupedProducts?: GroupedProducts
    goBackOnSuccess?: boolean
    suggestedCallFlow?: boolean
    meetingAt?: string
    coachId?: string
  }
  UpsellOffer?: {
    upsellOfferIndex: number
  }
  UpsellOfferConfirmation: undefined
  UnifiedOnboardingAppointmentOfferConfirmation: {
    nextScreen: {
      screen: keyof CommonAuthorizedStackParamList
      params?: any
    }
    coachingKind: 'stress' | 'nutrition'
  }
  AppExplorationCarousel: undefined
  AppExplorationPrompt: undefined
  PermissionsCarousel:
    | undefined
    | {
        nextScreen: { screen: 'AppExplorationPrompt'; params?: any }
      }
  ConfirmSensorSelection: undefined
  LibreIndirectSyncConfirmation: undefined
  OwnSensorSelection: {
    nextScreen:
      | 'ConfirmSensorSelection'
      | 'OwnSensorFreeTrialSubscriptionOffer'
      | 'LibreIndirectSyncConfirmation'
    params?: any
  }
  FreeVideoCall: undefined
}

export type AuthStackParamList = {
  SignIn: undefined
  SignInOrSignUp: undefined
  CreateAccount: undefined
  CreateAccountStelo: undefined
  CreateAccountOptions: undefined
  CreateAccountOptionsStelo: undefined
  PasswordResetRequest: undefined
  PasswordResetRequestSuccess: { email: string } | undefined
  PasswordReset: { passwordToken: string; email: string } | undefined
  ShowAdmin: undefined
  OnboardingSubscriptionOptions: undefined
  MfaChallenge: undefined
  UhcMemberAppAccessBlocked: undefined
  ReferralVideoCall: {
    coachUserId: string
    referredByUserId?: string
    status?: 'slot_not_available' | 'coach_not_licensed'
  }
  TrustpilotReview: {
    url: string
  }
  ReferralVideoCallReserved: {
    meetingAt: string
    coachFullName: string
    coachPhoto?: string | null
    coachCredentials?: string | null
    coachBio?: string | null
    coachSpecialties: Pick<EhrCoachSpecialtyDef, 'key' | 'name'>[]
    coachId: string
    coachUserId: string
    referringUserId?: string
  }
}

export type DashboardStackParamsList = {
  Home: { resetFilters?: boolean } | undefined
}

export type OnboardingStackParamsList = {
  // Params are optional: the screen can be reached via deep link without them,
  // so it renders a fallback when product is missing.
  EligibilityCheck:
    | {
        product?: Product
        savings?: number
        checkoutOptionKinds?: SubscriptionCheckoutOptionKind[]
        nextScreen?: { screen: 'Questionnaire' | 'OwnSensorSelection'; params: any }
      }
    | undefined
  SubscriptionCheckout: {
    product: Pick<
      Product,
      | 'id'
      | 'key'
      | 'requiresShipping'
      | 'appliedPromo'
      | 'trialDays'
      | 'title'
      | 'interval'
      | 'price'
    >
    subscriptionId?: string
    savings?: number
    trialEndsAt?: string | null
    checkoutKind?: CheckoutKind
    checkoutOptionKinds?: SubscriptionCheckoutOptionKind[]
    showMicrobiomeTest?: boolean
    nextScreen?: {
      screen: keyof CommonAuthorizedStackParamList
      params?: any
    }
  }
  ProductsLoading: undefined
  FreeTrialSubscriptionOffer: { product: Product; subscription?: Subscription }
  OwnSensorFreeTrialSubscriptionOffer: { product: Product }
  LibreIndirectSyncConfirmation: undefined
  GoalQuestionnaire: {
    questionnaire: SurveysConfigKind
    nextScreen?: {
      screen: 'ConfirmSensorSelection'
    }
    allowEarlyExit?: boolean
    showCompletedScreen?: boolean
    showEligibilitySuccessNote?: boolean
    skipIntroScreen?: boolean
  }
  MedicalDisclaimerQuestionnaire: {
    allowEarlyExit?: boolean
    close?: () => void
    questionnaire: SurveysConfigKind
    nextScreen?: { screen: 'SteloSelectSubscription' }
  }
  OnboardingInsuranceQuestionnaire: {
    skipIntroScreen?: boolean
    nextScreen?: { screen: 'InsuranceCheck' }
  }
  OnboardingExperienceBasicInformationQuestionnaire: {
    questionnaire: SurveysConfigKind.OnboardingExperienceBasicInformation
    nextScreen: { screen: 'UnifiedOnboardingGoal' }
  }
  WeightLossExperienceDesiredWeightCompleted: undefined
  WeightLossExperiencePersonalPlan: undefined
  InsuranceCheck: undefined
  InsuranceAddress: undefined
  PaidVideoCallsProductExplanation:
    | {
        meetingAt?: string
        coachId?: string
      }
    | undefined
  VideoCallConfirmation: undefined
  VideoCallCheckout:
    | {
        suggestedCallFlow?: boolean
        meetingAt?: string
        coachId?: string
        product?: VideoCallProduct | undefined
      }
    | undefined
  ReferralVideoCallNotLicensed: {
    meetingAt?: string
  }
  ReferralVideoCall: {
    coachUserId: string
    referredByUserId?: string
    status?: 'slot_not_available' | 'coach_not_licensed'
  }
  OnboardingSubscriptionOptions: undefined
  ProgramError: undefined
  SteloCheckout:
    | {
        checkoutKind?: CheckoutKind
        subscriptionId?: string
        product?: Product
      }
    | undefined
  SteloSelectSubscription:
    | undefined
    | {
        checkoutKind?: CheckoutKind
        subscriptionId?: string
      }
  UnifiedOnboardingStart: undefined
  UnifiedOnboardingVideoCallsStart: undefined
  UnifiedOnboardingGoal: undefined
  UnifiedOnboardingInsuranceIntro: undefined
  UnifiedOnboardingInsuranceProvider: undefined
  UnifiedOnboardingPostSchedulingInsurance: undefined
  UnifiedOnboardingPaidCallInfo: undefined
  UnifiedOnboardingFreeCallInfo: undefined | { reason: 'unsupported_provider' }
  UnifiedOnboardingCoachingTeam: undefined
  UnifiedOnboardingCoachingTeamStressUpsell: undefined
  UnifiedOnboardingSelectNutritionist:
    | undefined
    | {
        selectedSpecialties?: EhrCoachSpecialty[]
        coachingKind?: SpecialtyKey
        nextScreen?: {
          screen: string
          reset?: boolean
          params?: any
        }
      }
  CoachSwitchSpecialtiesFilterModal: {
    parentScreen: 'UnifiedOnboardingSelectNutritionist'
    selectedSpecialties: EhrCoachSpecialty[]
  }
  CallScheduling: {
    appointmentKind: EhrAppointmentKind
    allowBackNavigation?: boolean
    appointmentId?: string
    coachProfile?: AllCoachProfilesQuery['allCoachProfiles']['coachProfiles'][0]
    cadenceOptions?: { value: EhrAppointmentRecurrenceCadence; text: string }[]
    showCoachProfile?: boolean
    pendingAppointmentId?: string
    errorMessage?: string
    feeProduct?: Product | undefined
    switchingCoach?: boolean
    product?: VideoCallProduct | null | undefined
    insuranceKind?: UserInsurancePolicyKind
    nextScreen?: {
      screen: string
      reset?: boolean
      params?: any
    }
  }
  CallSchedulingReviewScreen: {
    selectedSlot: EhrSlot
    appointmentKind: EhrAppointmentKind
    product: VideoCallProduct | null | undefined
    cadence: EhrAppointmentRecurrenceCadence
    insuranceKind?: UserInsurancePolicyKind
  }
  InsuranceBenefitsCheck: undefined
}

export type SubscriptionsStackParamsList = {
  Marketplace: WithCancelModal | undefined
  // Params are optional: the screen can be reached via deep link without them,
  // so it renders a fallback when product is missing.
  EligibilityCheck:
    | {
        product?: Product
        savings?: number
        checkoutOptionKinds?: SubscriptionCheckoutOptionKind[]
        nextScreen?: { screen: 'Questionnaire' | 'OwnSensorSelection'; params: any }
      }
    | undefined
  SubscriptionOptions: undefined
}

export type DetailedPageUrlParams = {
  statSection: SectionEnum
  statMetaKey: ScoreSectionEnum | GlucoseKetonesEnum | MacrosSectionEnum | MeasurementEnum
  startDate: string
  endDate: string
}
export type InsightsStackParamsList = {
  InsightsDashboard: undefined
  InsightsDetailedPage:
    | { statistic: Statistic }
    | {
        statSection: SectionEnum
        statMetaKey: ScoreSectionEnum | GlucoseKetonesEnum | MacrosSectionEnum | MeasurementEnum
        startDate: string
        endDate: string
      }
  InsightsSettings: undefined
  ChooseGoalModal: undefined
  ChoosePrimaryGoalMetricsModal: undefined
}

export type TabParamList = {
  Home: { resetFilters?: boolean } | undefined
  Events: { resetFilters?: boolean } | undefined
  Plan: undefined
  MultiBar: undefined
  Insights: undefined
  Learn: undefined
  NutritionistHub: undefined
  Nora: undefined
}

export type DrawerParamList = {
  Dashboard: { screen: 'Dashboard' } | undefined
  Events: { resetFilters?: boolean } | undefined
  Home: { resetFilters?: boolean } | undefined
  Plan: undefined
  Education: undefined
  Account: (WithCancelModal & WithPhotoUpload) | undefined
  Settings: undefined
  Referral: ReferralParams
  Subscriptions: { screen: 'Marketplace' } | undefined
  Support: undefined
  Insights: undefined
  NutritionistHub: undefined
  FreeTrialSubscriptionOffer: { product: Product; subscription?: Subscription }
  Learn: undefined
  Nora: undefined
}

export interface SubscriptionParams {
  subscriptionId?: string
}

type ProductExplanationUpsellOfferParams = Pick<
  UpsellOffer,
  | 'benefits'
  | 'benefitsDescription'
  | 'benefitsTitle'
  | 'billingProduct'
  | 'billingProductName'
  | 'billingProductPrice'
  | 'subscriptionId'
>

export type AppStackParamList = {
  // onboarding
  Onboarding: {
    screen: string
    params?: ScreenAndProps
  }

  // outdated
  OutdatedScreen: undefined

  // auth
  Auth: undefined

  Walkthrough: undefined

  Scans: undefined

  CheckConnection: undefined | { fromTutorialId: TutorialSensorKind }
  ConnectionSuccessful: undefined
  SteloSetup: { mode: 'upgrade' | 'migration' }

  // other
  Drawer:
    | {
        screen: 'Dashboard'
        params?: { screen: 'Events' | 'NutritionistHub' }
      }
    | undefined
  AppSettings: undefined
  OpenSourceLicenses: undefined
  OpenSourceLicenseDetail: { id: string }
  FriendReferral: ReferralParams
  ProductExplanation:
    | {
        screen: ProductExplanationScreenTypes
      }
    | {
        upsellOffer: ProductExplanationUpsellOfferParams
      }
  Subscriptions: { screen: 'Marketplace' } | undefined
  ChangeDateRange: {
    type: Exclude<DateSelectorType, 'plan'>
    allowRangeSelection?: boolean
  }
  AddJournalEntry: (
    | {
        item?: JournalEntry
        id?: string
        body: string
        occurredAt?: string
        measurements?: JournalMeasurement[]
        saveTarget?: JournalEntrySaveTarget
      }
    | undefined
  ) &
    WithCancelModal
  EditJournalEntry: {
    item: JournalEntry
  } & WithCancelModal
  OrderConfirmed: undefined
  AddActivity:
    | ({
        item?: EditableActivity
        activityType?: ActivityType
        saveTarget?: ActivitySaveTarget
      } & WithCancelModal)
    | undefined
  ActivityDetail: { item: Activity }
  ActivitySource: undefined
  Marketplace: WithCancelModal | undefined

  /* Meals */
  AddMeal?: {
    shouldGoBack?: boolean
    mealKind?: MealKind
  } & WithPhotoUpload &
    WithCancelModal
  EditMeal: {
    id?: string
  } & WithPhotoUpload &
    WithCancelModal
  MealDetail: {
    item: Meal
  }
  CompareMeals: {
    initialMealIds: string
  }
  CompareMealsDetails: {
    mealIds: string
  }
  UnlockNutritionistSupport: undefined
  ShareMealComparison: {
    mealIds: string
  }
  CustomIngredient:
    | ({
        ingredientId?: string
        name?: string
      } & WithCancelModal)
    | undefined
  IngredientSearch: {
    createdIngredientId?: string
    mealType?: MealKind
  } & WithCancelModal
  BarcodeIngredientSearch: undefined
  IngredientPortion: {
    ingredient: BaseIngredient
    isUpdatingIngredient?: boolean
    saveTarget?: IngredientPortionSaveTarget
  }

  AddMeasurement:
    | ({
        item?: {
          externalSource?: HealthDataMeasurementSource
          id?: string
          occurredAt?: string
          type: HealthDataMeasurementType
          values?: MeasurementValues
        }
        type?: HealthDataMeasurementType
      } & WithCancelModal)
    | undefined
  EditMeasurement: { item: Measurement } & WithCancelModal
  EditDailyMeasurementModal: { date: string; type: HealthDataMeasurementType }
  ShowActions: undefined
  IntercomMenu: undefined
  ShowTutorial: { tutorial: TutorialStep[] } | undefined
  ZoomChart: { type: ChartTypes } & WithForceLandscapeOrientation
  GlucoseScoreModal: undefined
  NutritionSummaryModal: undefined
  TimelineTutorialModal: { timeline: 'Glucose' | 'Ketone' }
  NutritionDetails: undefined
  CalibrateSensor: undefined
  ChangeMacroGoals: { source: keyof RootStackParamList }
  ChangeFastingWindow: undefined
  ShareViewModal: {
    shareableViewName: ShareableViews
    item: Meal | Activity
  }
  AdminSettings: undefined
  DashboardEdit: undefined
  EventsEdit: undefined
  AccountSubscriptions: undefined
  EditAccountDetails: WithCancelModal | undefined
  ChangePassword: undefined
  EditEmail: undefined
  AddLabResults: { closeIcon?: boolean } | undefined
  LabResultDetail: { id: string }
  UploadLabResults: { file?: ReactNativeFile }
  UploadLabResultsSuccess: undefined
  LabResultsScreen: undefined
  BiomarkersScreen: undefined
  BiomarkerForm: {
    id?: string
    biomarkerName: string
    biomarkerKind: string
    value?: number
    date?: string
    unit?: string
    nextScreen: 'BiomarkersScreen' | 'LabResultDetail' | 'Chat' | null
    nextScreenParams: any
  }
  OnboardingChecklist: undefined
  ProductsSelection: {
    addons: Product[]
    selectedAddon: Product
  }
  SteloAddonOffer: {
    steloProducts: Product[]
  }
  ConfirmSensorSelection: undefined
  HealthPermission: undefined
  ProgramDetails: SubscriptionParams
  ManageSubscriptions: undefined
  PurchaseFreeByosProduct: {
    product: Product
    subscription: Subscription
  }
  CancelSubscriptionOtherOptions: SubscriptionParams
  CancelSubscriptionOnCommitment: SubscriptionParams
  CancelSubscriptionConfirmation: SubscriptionParams & {
    showInsuranceCallsUpsell?: string
  }
  ChurnRecoveryOptions: SubscriptionParams & {
    billingProductCategory: BillingProductCategory
    title: string
    description: string
    notes: string
    upsellOfferLink: string
  }
  ChurnRecoveryUpsellOffer: SubscriptionParams & {
    billingProduct: BillingProduct
    title: string
    description: string
    billingProductPrice: string
    billingProductIntervalDescription: string
    offerPillText: string
    offerHeaderText: string
    offerSubheaderText: string
    offerFooterText: string
    showCommitmentDisclaimer: boolean
  }
  ChurnRecoveryUpsellOfferConfirmation: {
    subscriptionId: string
    billingProduct: BillingProduct
    billingProductPrice: string
    billingProductIntervalDescription: string
  }
  ChurnRecoveryMembershipOffer: {
    subscriptionId: string
    data: string
    showBackButton?: boolean
  }
  ChurnMonthlyCGMOffer: {
    nextScreenParams: string
  }
  InsuranceCallsUpsellScreen: {
    showSuccessAlert?: boolean
  }
  SubscriptionCheckout: {
    product: Pick<
      Product,
      | 'id'
      | 'key'
      | 'requiresShipping'
      | 'appliedPromo'
      | 'trialDays'
      | 'title'
      | 'interval'
      | 'price'
    >
    subscriptionId?: string
    savings?: number
    trialEndsAt?: string | null
    checkoutKind?: CheckoutKind
    checkoutOptionKinds?: SubscriptionCheckoutOptionKind[]
    showMicrobiomeTest?: boolean
    nextScreen?: {
      screen: keyof CommonAuthorizedStackParamList
      params?: any
    }
  }
  PauseSubscription: SubscriptionParams
  PauseSubscriptionReview: SubscriptionParams & {
    cycleCount: number
  }
  PauseSubscriptionSuccess: undefined
  ResumeSubscriptionModal: undefined
  ReactivateSubscriptionModal: SubscriptionParams
  ChangeBillingCycleModal: SubscriptionParams
  ChangeBillingCycleSuccessModal: SubscriptionParams
  ManageUpcomingSubscriptionModal: undefined
  UserSettings: undefined
  NotificationInbox: undefined
  SensorSettings: undefined
  DexcomConnectionSuccessful: { showAppExplorationPrompt?: boolean } | undefined
  DexcomSensorSettings: { autoToggleOn?: boolean; sensorModel?: SensorModel } | undefined
  NotificationsSettings: undefined
  AddReminderModal: { reminderKind?: ReminderKind } | undefined
  EditReminderModal: {
    reminder: Reminder
    reminderConfig: ReminderKind
  }
  ThresholdSettings: { type: ThresholdType; source: keyof RootStackParamList }
  GlucoseThresholdSettings: { source: keyof RootStackParamList }
  CustomizeHomeModal: undefined
  Integrations: WithCancelModal | undefined
  MfpInstructions: undefined
  TerraFitbitGoogleHealthAnnouncement: undefined
  TerraProvider: {
    provider: string
    resource?: string
    reason?: string
  } & WithCancelModal
  SourcePriorityList: {
    measurementType: HealthDataMeasurementType
  }
  TutorialsGroup: {
    group: TutorialGroups
  }
  ActivateSensorTutorialsGroup: {
    group: TutorialGroups
  }
  CMSTutorial: {
    tutorialId: string
  }
  VideoTutorial: {
    url: string
    title: string
    description: string
  }
  HealthKitIntegration: undefined
  KetoMojoIntegration: undefined
  EducationModal: undefined
  RequiredPolicyConsent: { nextScreen?: ScreenAndProps }
  PolicyConsent: {
    policy: LegalPolicy
    nextScreen?: ScreenAndProps
  }
  SkipTutorial: undefined
  GlucoseEducation: undefined
  CancelModal: {
    title: string
    cancelText?: string
    confirmText?: string
    parentScreen:
      | 'AddActivity'
      | 'AddMeal'
      | 'EditMeal'
      | 'CustomIngredient'
      | 'AddMeasurement'
      | 'IngredientSearch'
      | 'Marketplace'
      | 'Integrations'
      | 'TerraProvider'
      | 'EditAccountDetails'
    confirmScreen?: 'CustomIngredient' | 'IngredientSearch'
    confirmScreenParams?: any
    cancelScreen?: 'BarcodeIngredientSearch' | 'IngredientSearch' | 'ProgramDetails'
    cancelScreenParams?: any
    replace?: boolean
  }
  RateOurAppModal: undefined
  RateAppModal: undefined
  PositiveCoachRatingRateAppModal: undefined
  PositiveRatingModal: undefined
  GiveFeedbackModal: {
    questionnaire: SurveysConfigKind
    questionsCount: number
    durationInMinutes: number
  }
  Questionnaire: {
    questionnaire: SurveysConfigKind
    nextScreen?: ScreenAndProps & { pop?: boolean }
    allowEarlyExit?: boolean
    close?: () => void
    skipProgressIndicator?: boolean
    hideNavigationBar?: boolean
    showCompletedScreen?: boolean
    showEligibilitySuccessNote?: boolean
    skipIntroScreen?: boolean
    forceStart?: boolean
  }
  InsuranceQuestionnaire?: {
    // Onboarding (and other flows that own their own routing) can override the
    // backend-resolved completionDeepLink to send the user to a specific
    // post-completion screen — e.g. UnifiedOnboardingConfirmation. Set
    // `pop: true` to pop back to that screen on the existing stack instead of
    // replacing — preserves an underlying screen's state (e.g. returning to
    // CallSchedulingReviewScreen after collecting insurance mid-flow).
    nextScreen?: ScreenAndProps & { pop?: boolean }
    // Skip the intro screen when the caller has already framed the flow for
    // the user (e.g. coming from InsuranceBenefitsCheck inside onboarding).
    skipIntroScreen?: boolean
    // Hide the per-question step counter; useful in onboarding where the
    // surrounding flow already conveys progress. The navigation bar collapses
    // to a plain safe-area inset when there's nothing else to show in it.
    skipProgressIndicator?: boolean
    // Show a close (X) button in the navigation bar that lets the user exit
    // the questionnaire without submitting.
    allowEarlyExit?: boolean
    // Pre-populate form values keyed by SurveysQuestionKey. When provided,
    // any prior persisted MMKV state is cleared and the form starts from the
    // prefill — so a stale partial answer from a previous session can't
    // shadow the fresh values. The user lands on the first visible question
    // whose key isn't in the prefill (or the last question if all are
    // already answered, so they get a confirm-and-submit step).
    prefill?: Record<string, unknown>
  }
  CoursesLearn: undefined
  CoursesProgram?: {
    programId: string
  }
  CoursesModule: CoursesModuleParams
  CoursesLesson: {
    lesson: LessonWithContent
    parentScreenParams: CoursesModuleParams
  }
  CoursesLessonItem: {
    lesson: LessonWithContent
    index: number
    parentScreenParams: CoursesModuleParams
  }
  CoursesModuleCompletion: {
    title: string
    isProgramComplete: boolean
    nextModuleParams: CoursesModuleParams
  }
  CoursesExperiment: {
    lesson: LessonWithContent
    parentScreenParams: CoursesModuleParams
  }
  JournalEngagementPrompt: undefined
  WebViewModal: {
    uri: string
  }
  Chat:
    | {
        cannedPromptKey?: string
      }
    | undefined
  NoraConversationScreen:
    | { conversation: ChatbotConversation }
    | { conversationProviderId: string }
    | {
        initialPrompt?: string
        initialVoiceModeEnabled?: boolean
        initialVoiceModeSource?: NoraVoiceModeSource
      }
    | undefined
  NoraConversationsScreen: { filter?: HistoryView } | undefined
  NoraQuickPromptsScreen: undefined
  NoraReportScreen: { report: HealthDataReport } | { reportId: string }
  NoraReportsScreen: undefined
  NoraChatPreferencesSettingsScreen: undefined
  NoraNotificationSettingsScreen: undefined
  NoraSettingsScreen: undefined
  ChatWelcomeModal: undefined
  ChatDelimiterConfirmationModal: {
    channelId: string
  }
  CallScheduling: {
    appointmentKind: EhrAppointmentKind
    alertMessage?: string
    allowBackNavigation?: boolean
    appointmentId?: string
    cadenceOptions?: { value: EhrAppointmentRecurrenceCadence; text: string }[]
    coachProfile?: AllCoachProfilesQuery['allCoachProfiles']['coachProfiles'][0]
    showCoachProfile?: boolean
    pendingAppointmentId?: string
    errorMessage?: string
    feeProduct?: Product | undefined
    switchingCoach?: boolean
    product?: VideoCallProduct | null | undefined
  }
  CoachSwitchSeeAllCoachesScreen: CoachSwitchSeeAllCoachesScreenParams
  CallSchedulingSeeAllCoachesScreen: CallSchedulingSeeAllCoachesScreenParams
  CoachSwitchSummaryScreen: {
    coachProfile?: AllCoachProfilesQuery['allCoachProfiles']['coachProfiles'][0]
    coachId?: string
    appointmentKind?: EhrAppointmentKind
    cadence?: EhrAppointmentRecurrenceCadence
    selectedSlot?: EhrSlot | null
    userInitiated: boolean
  }
  CoachSwitchSpecialtiesFilterModal: CoachSwitchSpecialtiesFilterModalParams
  RescheduleVideoCallToEarlierSlot: {
    appointmentKind: EhrAppointmentKind
    appointmentId: string
    nutritionistName?: string | null
    title?: string | null
    date: string
  }
  SetWeightLossGoal: undefined
  ConfirmWeightLossRecurrence: {
    weightLossRate: number
    currentWeight: number
  }
  VideoCallModal: {
    id: string
  }
  NutritionistSwitchModal: { userInitiated?: boolean } | undefined
  NutritionistSwitchScreen:
    | {
        userInitiated?: boolean
        coachingKind?: CoachesCoachingKind
      }
    | undefined
  RescheduleOrCancelVideoCallModal: {
    videoCallId: string
    appointmentKind: EhrAppointmentKind
    nutritionistName?: string | null
    recurringAppointment?: boolean | null
    appointmentDate: string
  }
  CancelRecurringAppointmentModal: {
    videoCallId: string
    appointmentDate: string
  }
  LateRescheduleOrCancelVideoCallModal: {
    videoCallId: string
    appointmentKind: EhrAppointmentKind
    nutritionistName?: string | null
    lateCancellationFeeProduct: BillingProduct
  }
  LateCancelOrReschedulePaidVideoCallModal: {
    videoCallId: string
    useRescheduleDescription?: boolean
  }
  FreePassLateRescheduleOrCancelVideoCallModal: {
    videoCallId: string
    appointmentKind: EhrAppointmentKind
    nutritionistName?: string | null
    lateCancellationFeeProduct: BillingProduct
  }
  LateRescheduleVideoCallModal: {
    videoCallId: string
    appointmentKind: EhrAppointmentKind
    lateRescheduleFeeProduct: BillingProduct
    coachingKind: CoachesCoachingKind
  }
  FreePassLateRescheduleVideoCallModal: {
    videoCallId: string
    appointmentKind: EhrAppointmentKind
    nutritionistName?: string | null
    lateRescheduleFeeProduct: BillingProduct
  }
  VideoCallsStartModal: undefined
  NoMoreCallsLeftModal: { nextBillingDate?: Date; recurring: boolean }
  CoachingCallCreditBalanceModal: undefined
  UhcSubscriptionExpiredModal: undefined
  FeePaymentSuccessfulModal: undefined
  FeePaymentIncompleteModal: undefined
  RateCoachModal: { appointmentId?: string | null }
  ChurnFeedbackModal: undefined
  SetReminderModal: {
    dismissButtonText: string
    goBackOnDismiss: boolean
  }
  PaymentFailed: {
    videoCallId: string
    feeProduct: Product | undefined
    appointmentKind?: EhrAppointmentKind
  }
  FeePaymentScreen: {
    videoCallId: string
    feeProduct: Product | undefined
    appointmentKind?: EhrAppointmentKind
    selectedSlotDate?: Date
  }
  VideoCallsGettingStartedScreen: undefined
  PaidCallsIntroScreen: undefined
  CallSchedulingReviewScreen: {
    selectedSlot: EhrSlot
    appointmentKind: EhrAppointmentKind
    product: VideoCallProduct | null | undefined
    cadence: EhrAppointmentRecurrenceCadence
    insuranceKind?: UserInsurancePolicyKind
  }
  CallSchedulingConfirmationScreen: {
    appointmentId: string
  }
  VideoCallCheckout:
    | {
        suggestedCallFlow?: boolean
        meetingAt?: string
        coachId?: string
        product?: VideoCallProduct | undefined
      }
    | undefined
  ClaimFreeVideoCallModal: {
    appointmentId: string
    appointmentKind: EhrAppointmentKind
  }
  InsuranceNotEligibleModal: undefined
  ActionBottomSheet: undefined
  ScheduleCallOrPurchasePlanModal: {
    appointmentKind: EhrAppointmentKind
  }
  MonthlyNutritionCoachingCheckout: undefined
  ReferralVideoCallNotLicensed: {
    meetingAt?: string
  }
  ReferralVideoCall: {
    coachUserId: string
    referredByUserId?: string
    status?: 'slot_not_available' | 'coach_not_licensed'
  }
  MemberNoteDetail: {
    noteId: string
    chatConversationIntercomId?: string
    coachingKind?: CoachesCoachingKind
  }
  MemberNotes: undefined
  AllProducts: undefined
  VideoCallsUpsellModal: undefined
  VideoCallsUpsellDetailsModal:
    | {
        details?: { title?: string; body: string }[]
        description?: { emphasis?: string; body: string }
      }
    | undefined
  VideoCallsSteloUpsell: undefined
  ReferredToPurchasedByNutritionistModal: undefined
  NutritionGuidanceUpsellModal: undefined
  FreeNutritionistConsultationModal: undefined
  OwnSensorActivatedNutritionistUpsellModal: undefined
  AllVideoCalls: undefined
  ActionGoalForm:
    | {
        mode: 'create'
        actionGoalOption: ActionGoalOption
        initialValues?: {
          metricKind?: ActionGoalMetricKind
          metricValue?: number | null
          numberDays?: number
          goalText?: string
        }
        saveTarget?: ActionGoalSaveTarget
      }
    | {
        mode: 'edit'
        actionGoalOption: ActionGoalOption
        actionGoal: ActionGoal
        completions: ActionGoalCompletion[] | null | undefined
      }
    | {
        mode: 'view'
        actionGoalOption: ActionGoalOption
        actionGoal: ActionGoal
        completions: ActionGoalCompletion[] | null | undefined
      }
  ActionGoalListScreen: undefined
  UpsertRecurrence: {
    appointmentRecurrence: EhrAppointmentRecurrence | null
    insurancePolicy: InsurancePolicy | null
    proposed?: boolean
  }
  InsuranceStatus: undefined
  UpsertRecurrenceSuccessfulModal: undefined
  VideoCallCheckIn: {
    appointmentId: string
  }
  VideoCallCheckInAddendum: {
    appointmentId: string
  }
  CheckInCompletedModal: undefined
  SubmitInsuranceCardScreen: undefined
  Events: { resetFilters?: boolean } | undefined
  EventsListScreen: { resetFilters?: boolean } | undefined
  Home: { resetFilters?: boolean } | undefined
  // Params are optional: the screen can be reached via deep link without them,
  // so it renders a fallback when product is missing.
  EligibilityCheck:
    | {
        product?: Product
        checkoutOptionKinds?: SubscriptionCheckoutOptionKind[]
        savings?: number
        nextScreen?: { screen: 'Questionnaire' | 'OwnSensorSelection'; params: any }
      }
    | undefined
  InsurancePolicyForm: undefined
  SteloCheckout: {
    checkoutKind?: CheckoutKind
    subscriptionId?: string
    product?: Product
  }
  SteloEligibility: {
    nextScreen: { screen: any; params: any }
  }
  SteloSelectSubscription:
    | undefined
    | {
        checkoutKind?: CheckoutKind
        subscriptionId?: string
      }
  ProgramError: undefined
  SendMessageToCoach: { messageTemplateKind: ChatConversationMessageTemplateKind }

  // Stress Management
  StressManagementIntroScreen:
    | undefined
    | {
        source: 'banner' | 'addons' | 'scheduler'
      }

  CompletedScreen: {
    nextScreen?: string
    nextScreenParams?: any
    title?: string
    header?: string
    introMarkdown?: string
    icon?: UiIcon
    iconColor?: UiColor
    showCoachPhoto?: boolean
    primaryActionText?: string
    secondaryActionLink?: Link
    footnote?: string
    coachPhotoUri?: string
  }
  ScheduleNextCallModal: undefined
  UpsertQuickPromptModal: { id?: string; title?: string; text?: string } | undefined
  ReportNoteModal: { botMessageId: string; reason: ChatbotMessageReportReason }
  CustomReportModal: undefined
  LabPanelsScreen: undefined
  LabPanelsUpsellModal: undefined
  FreeCallTrialUpsellPopup: undefined
  MicrobiomeTest: {
    nextScreen: {
      screen: 'DietitianMenuCheckout' | 'SubscriptionCheckout'
      params?: any
    }
    upsell?: boolean
  }
  UnifiedOnboardingSelectNutritionist:
    | undefined
    | {
        selectedSpecialties?: EhrCoachSpecialty[]
        coachingKind?: SpecialtyKey
        nextScreen?: {
          screen: string
          reset?: boolean
          params?: any
        }
      }
}

export enum QuestionnaireScreenName {
  GoalsQuestionnaireStart = 'GoalsQuestionnaireStart',
  InsuranceQuestionnaireStart = 'InsuranceQuestionnaireStart',
  WeightLossExperienceDesiredWeightStart = 'WeightLossExperienceDesiredWeightStart',
  CoachingGoalsAndPreferencesStartScreen = 'CoachingGoalsAndPreferencesStartScreen',
  QuestionnaireSectionStart = 'QuestionnaireSectionStart',
  QuestionnaireQuestion = 'QuestionnaireQuestion',
  QuestionnaireCompleted = 'QuestionnaireCompleted',
  QuestionnaireLoading = 'QuestionnaireLoading',
  HealthQuestionnaireWarningModal = 'HealthQuestionnaireWarningModal',
  QuestionnaireRejection = 'QuestionnaireRejection',
  InsuranceQuestionnaireCompleted = 'InsuranceQuestionnaireCompleted',
  InsuranceEarlyExitCompleted = 'InsuranceEarlyExitCompleted',
}

export type QuestionnaireStackParamList = {
  [QuestionnaireScreenName.GoalsQuestionnaireStart]:
    | {
        programType: ProgramType
      }
    | undefined
  [QuestionnaireScreenName.QuestionnaireSectionStart]: {
    currentItemIndex: number
    shouldIncrementItemIndex: boolean
  }
  [QuestionnaireScreenName.QuestionnaireQuestion]: { currentItemIndex: number }
  [QuestionnaireScreenName.QuestionnaireCompleted]:
    | undefined
    | { nextScreen?: string; nextScreenParams?: any }
  [QuestionnaireScreenName.QuestionnaireLoading]:
    | undefined
    | {
        error?: boolean
        errorMessage?: string
      }
  [QuestionnaireScreenName.HealthQuestionnaireWarningModal]: undefined
  [QuestionnaireScreenName.QuestionnaireRejection]: {
    outcome: Outcome
    nextScreen?: string
    nextScreenParams?: any
  }
  [QuestionnaireScreenName.InsuranceQuestionnaireCompleted]:
    | undefined
    | { nextScreen?: string; nextScreenParams?: any; isUnsupported?: boolean }
  [QuestionnaireScreenName.InsuranceEarlyExitCompleted]: undefined
  [QuestionnaireScreenName.InsuranceQuestionnaireStart]:
    | { allowEarlyExit: boolean; showEligibilitySuccessNote: boolean }
    | undefined
  [QuestionnaireScreenName.WeightLossExperienceDesiredWeightStart]: undefined
  [QuestionnaireScreenName.CoachingGoalsAndPreferencesStartScreen]: undefined
}

export type RootStackParamList = CommonStackParamList &
  CommonAuthorizedStackParamList &
  AuthStackParamList &
  DashboardStackParamsList &
  OnboardingStackParamsList &
  TabParamList &
  DrawerParamList &
  AppStackParamList &
  QuestionnaireStackParamList &
  InsightsStackParamsList

export type DrawerRouteProp<T extends keyof DrawerParamList> = RouteProp<DrawerParamList, T>
export type AppRouteProp<T extends keyof AppStackParamList> = RouteProp<AppStackParamList, T>
export type InsightsRouteProp<T extends keyof InsightsStackParamsList> = RouteProp<
  InsightsStackParamsList,
  T
>

export type CommonAuthorizedRouteProps<T extends keyof CommonAuthorizedStackParamList> = RouteProp<
  CommonAuthorizedStackParamList,
  T
>
export type AuthRouteProp<T extends keyof AuthStackParamList> = RouteProp<AuthStackParamList, T>
